
import React, { useEffect, useState } from 'react';
import { AxiosError } from "axios";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { saveData } from 'services/api/saveData';
import Dropdown from 'components/forms/basefields/Dropdown';
import { Division } from '../../../views/environments/EnvironmentTypes';
import { useFetchData } from 'services/api/useFetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { useGlobalContext } from 'GlobalContext';
import BasicMailExample from '../../../assets/mail-example-basic.png';
import ProfessionalMailExample from '../../../assets/mail-example-professional.png';
import TextInput from 'components/forms/basefields/TextInput';

const EmailLayoutLibrary: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { setFloatingAlert } = useGlobalContext();
    const { activeFeatures } = useAuthContext();
    const [name, setName] = useState('');
    const [showNameError, setShowNameError] = useState<boolean>(false);
    const [divisions, setDivisions] = useState<Division[]>([]);

    // Prefill the division with the division selected in the settings menu
    const { selectedDivision: settingsDivision, isEnvironmentSelected } = useSettingsDivision();
    const [selectedDivision, setSelectedDivision] = useState<number | null>(
        !isEnvironmentSelected && settingsDivision ? settingsDivision.id : null
    );

    // Fetch the divisions
    const { response: fetchedDivisions } = useFetchData({ 
        apiUrl: 'get_division_list',
        useGuard: true,
        guardValue: activeFeatures.includes('divisions')
    });

    // Set the fetched divisions
    useEffect(() => {
        if (fetchedDivisions) {
            setDivisions(fetchedDivisions.results);
        }
    }, [fetchedDivisions]);

    // Set the email layout options
    const emailLayoutOptions = [
        {
            style: 'basic',
            title: 'email.email_layout.style.basic',
            description: 'email.email_layout.basic_style_description',
            image: BasicMailExample
        },
        {
            style: 'professional',
            title: 'email.email_layout.style.professional',
            description: 'email.email_layout.professional_style_description',
            image: ProfessionalMailExample
        }
    ];

    // Handle the creation of an e-mail layout and redirect to it
    const handleCreateEmailLayout = async (style: string) => {

        // Show floating alert if name is not entered
        if (!name || name.trim() === '') {
            setShowNameError(true);
            setFloatingAlert({
                type: 'danger',
                message: t('email.email_layout.name_required')
            });
            return;
        } else {
            setShowNameError(false);
        }

        // Show floating alert if feature is not active
        if (!activeFeatures.includes('email_layouts')) {
            setFloatingAlert({
                type: 'danger',
                message: t('email.email_layout.feature_not_available')
            });
            return;
        }

        try {
            // Create the email layout in the backend
            const response = await saveData({ 
                apiUrl: 'post_email_layout',
                method: 'post',
                data: { 
                    style: style,
                    name: name,
                    division: selectedDivision
                }
            });

            // Redirect to the email layout page
            if (response?.status == 201) {
                history.push(`/settings/features/email/layout/${response.data.id}`);
            }
        // Show floating alert in case of error
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.data) {
                setFloatingAlert({ type: 'danger', message: t(error.response.data) })
            }
                        
            return null;
        }
    };
    
    return (
        <div className='detailpage library-page email-layout-library container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button'
                          onClick={() => history.goBack()}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>
                        {t('email.email_layout.add_email_layout_header')}
                    </h2>
                </div>
            </div>
            <div className='' style={{ marginTop: '2.5rem' }}>
                <div className='select-division'
                     style={{ marginBottom: '0.5rem' }}>
                    <h4>
                        {t('email.email_layout.layout_name_label')}
                    </h4>
                    <div className='edit-mode'>
                        <label>
                            {t('email.email_layout.layout_name_description')}
                        </label>
                        <TextInput
                            name='name'
                            value={name}
                            placeholder='email.email_layout.layout_name_placeholder'
                            onChange={(value) => setName(value)}
                            shouldAutoFocus={true}
                            isInvalid={showNameError}
                        />
                    </div>
                </div>
                {activeFeatures.includes('divisions') && (
                    <div className='select-division'>
                        <h4>
                            {t('email.email_layout.choose_division_label')}
                        </h4>
                        <div className='edit-mode'>
                            <label>
                                {t('email.email_layout.choose_division_description')}
                            </label>
                            <Dropdown<Division>
                                options={divisions?.filter(division => division.is_active) || []}
                                id='division'
                                name='division'
                                disabled_selected={t('email.email_template.division_disabled_selected')}
                                selectedOption={divisions.find(division => selectedDivision === division.id)}
                                onChange={(selectedValue) => setSelectedDivision(parseFloat(selectedValue))}
                                selectionFormat={(option) => `${option.name}`}
                                optionFormat='name'
                                showSearch={false}
                                allowNoneOption={true}
                            />
                        </div>
                    </div>
                )}
                <div className='library-group'>
                    <h4>
                        {t('email.email_layout.select_layout_style_label')}
                    </h4>
                    <div className='library-cards'> 
                        {emailLayoutOptions.map((option, index) => {
                            return (
                                <div key={index}
                                     onClick={() => handleCreateEmailLayout(option.style)}
                                     className='library-card'>
                                    <div className='title'>
                                        <h5>{t(option.title)}</h5>
                                    </div>
                                    <div className='example-image'>
                                        <img src={option.image} alt={`option.image`} />
                                    </div>
                                    <div>
                                        <p>{t(option.description)}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
};

export default EmailLayoutLibrary;